import React from "react";
import { useNavigate } from "react-router";

import { Delete as DeleteIcon, Refresh as RefreshIcon } from "@mui/icons-material";

import { OrderStatus, PurchaseOrderSummary, ResponseError, UserType } from "../apiClients/common";
import { deletePurchaseOrder, getPurchaseOrders, PurchaseOrderDetailsCollection } from "../apiClients/purchaseOrders";
import { AuthContext } from "../components/AuthContextProvider";
import { PurchaseOrderList } from "../components/PurchaseOrderList";
import { OrderPath } from "../constants";
import { useBulkMutationCall } from "../hooks/useBulkMutationCall";
import { useQueryCall } from "../hooks/useQueryCall";
import { Action } from "../lib/ActionBar";
import { ConfirmDialog } from "../lib/ConfirmDialog";
import { FilterType } from "../lib/Filter";
import { ListingFilterProps, SelectMode } from "../lib/Listing";
import { NotificationManagerContext } from "../lib/NotificationManager";
import { NotificationType } from "../lib/NotificationTypes";
import { Page } from "../lib/Page";
import Messages from "../locale/en.json";

enum FilterIds {
  From = "From",
  To = "To",
  Search = "Search",
}

enum ActionIds {
  Refresh = "Refresh",
  Delete = "Delete",
}

enum PanelIds {
  Delete = "Delete",
}

export const ActiveOrders = (): JSX.Element => {
  const auth = React.useContext(AuthContext);
  const notificationManager = React.useContext(NotificationManagerContext);

  const navigate = useNavigate();

  const [search, setSearch] = React.useState<string>("");

  const [processingPOs, setProcessingPOs] = React.useState<PurchaseOrderDetailsCollection>();
  const [printingPOs, setPrintingPOs] = React.useState<PurchaseOrderDetailsCollection>();

  const [selectedPurchaseOrders, setSelectedPurchaseOrders] = React.useState<PurchaseOrderDetailsCollection>();

  const [openPanel, setOpenPanel] = React.useState<PanelIds>();
  const closePanels = (): void => {
    setOpenPanel(undefined);
  };

  const purchaseOrders = React.useMemo(
    () => [...(printingPOs ?? []), ...(processingPOs ?? [])],
    [JSON.stringify(printingPOs), JSON.stringify(processingPOs)]
  );

  const { loading: processingPOsLoading, refresh: refreshProcessingPOs } = useQueryCall({
    method: getPurchaseOrders,
    args: { status: OrderStatus.Processing, search },
    onSuccess: setProcessingPOs,
    onFailure: (err?: ResponseError) => {
      if (err) {
        if (err?.status === 401) {
          auth.logout();
        } else {
          notificationManager.push({
            type: NotificationType.Error,
            title: Messages.errors.loadProcessingOrders,
            message: err.message,
          });
        }
      }
    },
  });

  const { loading: printingPOsLoading, refresh: refreshPrintingPOs } = useQueryCall({
    method: getPurchaseOrders,
    args: { status: OrderStatus.Printing, search },
    onSuccess: setPrintingPOs,
    onFailure: (err?: ResponseError) => {
      if (err) {
        if (err?.status === 401) {
          auth.logout();
        } else {
          notificationManager.push({
            type: NotificationType.Error,
            title: Messages.errors.loadPrintingOrders,
            message: err.message,
          });
        }
      }
    },
  });

  const refresh = (): void => {
    refreshPrintingPOs();
    refreshProcessingPOs();
  };

  const { loading: deleteLoading, invoke: invokeDelete } = useBulkMutationCall({
    method: deletePurchaseOrder,
    onSuccess: refresh,
    onFailure: (error?: ResponseError[]) => {
      if (error?.length) {
        if (error.find((e) => e?.status === 401)) {
          auth.logout();
        } else {
          notificationManager.push({
            type: NotificationType.Error,
            title: Messages.errors.deleteOrder,
            message: error
              .map((err) => err?.message)
              .filter((message) => !!message)
              .join(" "),
          });
        }
      }
    },
  });

  const actions: Action[] = [
    {
      id: ActionIds.Refresh,
      disabled: printingPOsLoading || processingPOsLoading,
      value: <RefreshIcon />,
      onClick: refresh,
    },
  ];

  if (auth.user?.type === UserType.SuperAdmin || auth.user?.type === UserType.Admin) {
    actions.push({
      id: ActionIds.Delete,
      value: <DeleteIcon />,
      disabled: selectedPurchaseOrders?.length !== 1 || deleteLoading,
      onClick: () => setOpenPanel(PanelIds.Delete),
    });
  }

  const navigateToPO = ({ id }: PurchaseOrderSummary): void => {
    navigate(`/${OrderPath}/${id}/`);
  };

  const getPanel = (): JSX.Element | null => {
    switch (openPanel) {
      case PanelIds.Delete: {
        let messagePrefix = "";

        if (selectedPurchaseOrders?.length) {
          if (selectedPurchaseOrders.length > 1) {
            messagePrefix = Messages.confirm.deletePurchaseOrders;
          } else {
            messagePrefix = Messages.confirm.deletePurchaseOrder;
          }
        }

        const message = messagePrefix + selectedPurchaseOrders?.map((po) => po.purchaseOrderNumber ?? po.id).join(", ");

        return (
          <ConfirmDialog
            message={message}
            onCancel={closePanels}
            onConfirm={() => {
              const poIds = selectedPurchaseOrders?.map(({ id }) => ({ id })) ?? [];
              invokeDelete(poIds);
              closePanels();
            }}
          />
        );
      }
      default:
        return null;
    }
  };

  const filters = [
    {
      id: FilterIds.Search,
      type: FilterType.Search,
      defaultValue: search,
      onChange: setSearch,
    },
  ];

  const getOnSelect = (type?: UserType): ((items: PurchaseOrderSummary[]) => void) | undefined => {
    switch (type) {
      case UserType.SuperAdmin:
      case UserType.Admin:
        return setSelectedPurchaseOrders;
      case UserType.Client:
      default:
        return undefined;
    }
  };

  const getOnRowClick = (type?: UserType): ((item: PurchaseOrderSummary) => void) | undefined => {
    switch (type) {
      case UserType.SuperAdmin:
      case UserType.Admin:
      case UserType.Client:
        return navigateToPO;
      default:
        return undefined;
    }
  };

  return (
    <Page actions={actions} title={Messages.page.activeOrders.title}>
      <PurchaseOrderList
        loading={printingPOsLoading && processingPOsLoading}
        selectionMode={
          auth.user?.type === UserType.SuperAdmin || auth.user?.type === UserType.Admin
            ? SelectMode.Single
            : SelectMode.None
        }
        items={purchaseOrders}
        userType={auth.user?.type}
        filters={filters as ListingFilterProps<Date | string, PurchaseOrderSummary>[]}
        onSelect={getOnSelect(auth.user?.type)}
        onRowClick={getOnRowClick(auth.user?.type)}
      />
      {getPanel()}
    </Page>
  );
};
